import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  SelectChangeEvent,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import FileDownloadOutlined from "@mui/icons-material/FileDownloadOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

import agent from "../../utils/axios";
import { hasAnyRole, isAdmin } from "../../utils/auth";
import { formatDate, formatMoney } from "../../utils/format";
import { Payment1099 } from "../../types/payment1099";
import { PaymentDetail } from "../../types/paymentDetail";
import { formatMailingAddress } from "../../utils/address";
import { apiErrorMessage } from "../../utils/apiError";
import { saveBlob } from "../../utils/download";
import { visuallyHiddenSx } from "../../utils/visuallyHidden";
import { useTaxIdReveal } from "../../hooks/useTaxIdReveal";
import {
  Callout,
  DetailsExportCluster,
  FilterBar,
  MaskedTaxId,
  PageHeader,
  PayeeCell,
  Pdf1099Button,
  RevealAllButton,
  SearchField,
  StatusChip,
  useToast,
} from "../../components/ui";

// Fixed year for this component - 2025 only
const TAX_YEAR = 2025;

// Type for bill detail records
type BillDetail = {
  programName: string;
  invoiceDate: string;
  amount: number;
  clientBillId: string;
  taxable: boolean;
};

/**
 * Downloads the 2025 1099 PDF. The WHOLE row object, exactly as the API returned it, is
 * posted back: the server builds the PDF from it.
 */
function handlePdfDownload(
  row: Payment1099,
  onError: (message: string) => void
): void {
  agent.Tax2025.download1099PDF(row)
    .then((response: Blob) => {
      saveBlob(response, `${row.payeeName}_${row.year}_1099.pdf`);
    })
    .catch((error: unknown) => {
      onError(
        apiErrorMessage(
          error,
          `Couldn't download the 1099 for ${row.payeeName || "this payee"}.`
        )
      );
    });
}

// Expandable Row Component
interface RowProps {
  row: Payment1099;
  canReveal: boolean;
  revealed: boolean;
  onToggleReveal: (id: string) => void;
  canDownload: boolean;
  columnCount: number;
  isExpanded: boolean;
  isLoadingDetails: boolean;
  detailsError: boolean;
  details: BillDetail[];
  onToggleExpand: (id: string) => void;
  onPdfError: (message: string) => void;
}

function ExpandableRow({
  row,
  canReveal,
  revealed,
  onToggleReveal,
  canDownload,
  columnCount,
  isExpanded,
  isLoadingDetails,
  detailsError,
  details,
  onToggleExpand,
  onPdfError,
}: RowProps) {
  const address = formatMailingAddress(row);
  const detailsId = `payment-details-${row.id}`;
  const name = row.payeeName || "this payee";

  return (
    <React.Fragment>
      <TableRow hover sx={{ "& > *": { borderBottom: "unset" } }}>
        <TableCell sx={{ width: 56 }}>
          <IconButton
            aria-label={`${
              isExpanded ? "Hide" : "Show"
            } payment details for ${name}`}
            aria-expanded={isExpanded}
            aria-controls={detailsId}
            size="small"
            onClick={() => onToggleExpand(row.id)}
          >
            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ maxWidth: 280 }}>
          <PayeeCell
            name={row.payeeName}
            id={row.id}
            to={`/payees/detail/${row.id}`}
          />
        </TableCell>
        <TableCell>
          <MaskedTaxId
            value={row.taxId}
            canReveal={canReveal}
            revealed={canReveal ? revealed : false}
            onToggle={() => onToggleReveal(row.id)}
          />
        </TableCell>
        <TableCell
          align="right"
          sx={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}
        >
          {formatMoney(row.amount)}
        </TableCell>
        {canDownload && (
          <TableCell align="center">
            <Pdf1099Button
              payeeName={row.payeeName}
              onClick={() => handlePdfDownload(row, onPdfError)}
            />
          </TableCell>
        )}
        <TableCell sx={{ color: "text.secondary" }}>{address}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ py: 0 }} colSpan={columnCount}>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box id={detailsId} sx={{ my: 4, ml: 12, mr: 2 }}>
              <Typography
                variant="subtitle2"
                gutterBottom
                component="div"
                sx={{ fontWeight: 500, color: "text.secondary" }}
              >
                Payment Details
              </Typography>
              {isLoadingDetails && detailsError ? (
                <Typography variant="body2" color="error">
                  Couldn't load payment details. Reload the page to try again.
                </Typography>
              ) : isLoadingDetails ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">Loading details...</Typography>
                </Box>
              ) : details.length > 0 ? (
                <Table size="small" sx={{ maxWidth: 700 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Program</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell align="center">Taxable</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {details.map((detail, idx) => (
                      <TableRow
                        key={idx}
                        sx={
                          detail.taxable
                            ? undefined
                            : {
                                "& td": {
                                  textDecoration: "line-through",
                                  color: "text.disabled",
                                },
                              }
                        }
                      >
                        <TableCell>{detail.programName}</TableCell>
                        <TableCell>{formatDate(detail.invoiceDate)}</TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontVariantNumeric: "tabular-nums" }}
                        >
                          {formatMoney(detail.amount)}
                        </TableCell>
                        <TableCell align="center">
                          {detail.taxable ? (
                            <Typography
                              variant="body2"
                              component="span"
                              sx={{ color: "success.main", fontWeight: 500 }}
                            >
                              Yes
                            </Typography>
                          ) : (
                            <Typography
                              variant="body2"
                              component="span"
                              sx={{ color: "text.disabled" }}
                            >
                              No
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No bills found
                </Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

function Taxes2025() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [loadingButton, setLoadingButton] = useState(false);
  const [order, setOrder] = useState<"desc" | "asc">("asc");
  const [orderBy, setOrderBy] = useState("payeeName");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [data, setData] = useState<Payment1099[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  // Expanded rows state
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [billDetails, setBillDetails] = useState<Record<string, BillDetail[]>>(
    {}
  );
  const [loadingDetails, setLoadingDetails] = useState<Set<string>>(new Set());
  const [allPaymentDetails, setAllPaymentDetails] = useState<PaymentDetail[]>(
    []
  );
  const [detailsLoaded, setDetailsLoaded] = useState(false);
  const [detailsError, setDetailsError] = useState(false);
  const [paymentCountFilter, setPaymentCountFilter] = useState<string>("all");
  const [clientCountFilter, setClientCountFilter] = useState<string>("all");

  // Downloads and the 1099 column: Developer or Admin (unchanged).
  const canDownload = useMemo(() => hasAnyRole(["Developer", "Admin"]), []);
  // D1: only Admin may reveal a tax ID.
  const canReveal = useMemo(() => isAdmin(), []);
  const reveal = useTaxIdReveal();
  const requestSeq = useRef(0);
  const columnCount = canDownload ? 6 : 5;

  const notifyError = useCallback(
    (message: string) => toast.error(message),
    [toast]
  );

  const handlePaymentCountFilterChange = (event: SelectChangeEvent) => {
    setPaymentCountFilter(event.target.value);
  };

  const handleClientCountFilterChange = (event: SelectChangeEvent) => {
    setClientCountFilter(event.target.value);
  };

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(0); // Reset to first page on search
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Pre-fetch all payment details on component mount for faster accordion loading
  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const details: PaymentDetail[] =
          await agent.Tax2025.getAllPaymentDetailsByYear(TAX_YEAR.toString());
        setAllPaymentDetails(details);
        setDetailsLoaded(true);
      } catch {
        setDetailsError(true);
      }
    };

    fetchDetails();
  }, []);

  const handleToggleExpand = useCallback(
    (payeeId: string) => {
      setExpandedRows((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(payeeId)) {
          newSet.delete(payeeId);
        } else {
          newSet.add(payeeId);
        }
        return newSet;
      });

      // Get details from pre-fetched data (no filter on taxable - show all bills)
      if (!billDetails[payeeId]) {
        if (detailsLoaded) {
          const payeeBills = allPaymentDetails
            .filter((detail) => detail.payeeId === payeeId)
            .map((detail) => ({
              programName: detail.programName,
              invoiceDate: detail.paymentDate,
              amount: detail.amount,
              clientBillId: detail.payeeId,
              taxable: detail.taxable,
            }));

          setBillDetails((prev) => ({ ...prev, [payeeId]: payeeBills }));
        } else {
          // Details not yet loaded, show loading state
          setLoadingDetails((prev) => new Set(prev).add(payeeId));
        }
      }
    },
    [billDetails, detailsLoaded, allPaymentDetails]
  );

  // When details finish loading, populate any expanded rows that were waiting
  useEffect(() => {
    if (detailsLoaded && loadingDetails.size > 0) {
      const newBillDetails: Record<string, BillDetail[]> = {};
      loadingDetails.forEach((payeeId) => {
        const payeeBills = allPaymentDetails
          .filter((detail) => detail.payeeId === payeeId)
          .map((detail) => ({
            programName: detail.programName,
            invoiceDate: detail.paymentDate,
            amount: detail.amount,
            clientBillId: detail.payeeId,
            taxable: detail.taxable,
          }));
        newBillDetails[payeeId] = payeeBills;
      });

      setBillDetails((prev) => ({ ...prev, ...newBillDetails }));
      setLoadingDetails(new Set());
    }
  }, [detailsLoaded, loadingDetails, allPaymentDetails]);

  const fetchDataAndDownloadCSV = async () => {
    setLoadingButton(true);
    try {
      const { data } = await agent.Tax2025.getAllPaymentsByYear(
        TAX_YEAR.toString()
      );
      const sanitizedData = (data ?? []).map(
        ({ paymentStatus, pdfVisible, ...rest }: any) => rest
      );
      if (sanitizedData.length === 0) {
        toast.info(`There are no 1099 payments to download for ${TAX_YEAR}.`);
        return;
      }
      const headers = Object.keys(sanitizedData[0]).join(",");

      const rows = sanitizedData.map((row: any) =>
        Object.values(row)
          .map((value) => `"${value}"`)
          .join(",")
      );
      const csvContent = [headers, ...rows].join("\n");

      saveBlob(
        new Blob([csvContent], { type: "text/csv" }),
        `1099Payees${TAX_YEAR}_Tax2025.csv`
      );
    } catch (error) {
      toast.error(
        apiErrorMessage(error, "Couldn't download the 2025 1099 summary.")
      );
    } finally {
      setLoadingButton(false);
    }
  };

  const fetchDataAndDownloadDetailCSV = async () => {
    setLoadingButton(true);
    try {
      const detailedData: PaymentDetail[] =
        await agent.Tax2025.getAllPaymentDetailsByYear(
          TAX_YEAR.toString(),
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          paymentCountFilter,
          clientCountFilter
        );

      const paymentSuffix =
        paymentCountFilter === "multiple"
          ? "_MultiplePayments"
          : paymentCountFilter === "single"
          ? "_SinglePayment"
          : "";

      const clientSuffix =
        clientCountFilter === "compassAndOther" ? "_CompassAndOther" : "";

      const filterSuffix = paymentSuffix + clientSuffix;

      const headers =
        "Payee ID,Name,Address,Amount,Tax ID,Program Name,Taxable,Payment Date,Requesting Client";
      const rows = detailedData.map((row: PaymentDetail) =>
        [
          row.payeeId,
          row.payeeName,
          `${row.address1}${row.address2 ? `, ${row.address2}` : ""}, ${
            row.city
          }, ${row.state} ${row.postalCode}`,
          row.amount,
          row.taxId,
          row.programName,
          row.taxable ? "Yes" : "No",
          new Date(row.paymentDate).toLocaleDateString(),
          row.requestingClient,
        ]
          .map((value) => `"${value}"`)
          .join(",")
      );
      const csvContent = [headers, ...rows].join("\n");

      saveBlob(
        new Blob([csvContent], { type: "text/csv" }),
        `1099PayeesDetail${TAX_YEAR}${filterSuffix}_Tax2025.csv`
      );
    } catch (error) {
      toast.error(
        apiErrorMessage(error, "Couldn't download the 2025 payment details.")
      );
    } finally {
      setLoadingButton(false);
    }
  };

  // Fetch data with server-side pagination, sorting, and search
  useEffect(() => {
    const seq = ++requestSeq.current;
    async function getPayments() {
      setLoading(true);
      try {
        const response = await agent.Tax2025.getAllPaymentsByYear(
          TAX_YEAR.toString(),
          page,
          rowsPerPage,
          orderBy,
          order,
          debouncedSearchTerm
        );
        if (seq !== requestSeq.current) return;
        setData(response?.data ?? []);
        setTotalCount(response?.count ?? 0);
        setLoadError(false);
      } catch {
        if (seq !== requestSeq.current) return;
        setData([]);
        setTotalCount(0);
        setLoadError(true);
      } finally {
        if (seq === requestSeq.current) setLoading(false);
      }
    }

    getPayments();
  }, [page, rowsPerPage, debouncedSearchTerm, orderBy, order, reloadKey]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const retry = useCallback(() => setReloadKey((key) => key + 1), []);

  // Loading skeleton rows
  const renderSkeletonRows = () => {
    return Array.from({ length: 10 }).map((_, idx) => (
      <TableRow key={idx}>
        <TableCell>
          <Skeleton width={24} />
        </TableCell>
        <TableCell>
          <Skeleton width={150} />
          <Skeleton width={190} />
        </TableCell>
        <TableCell>
          <Skeleton width={100} />
        </TableCell>
        <TableCell>
          <Skeleton width={80} sx={{ ml: "auto" }} />
        </TableCell>
        {canDownload && (
          <TableCell>
            <Skeleton width={64} sx={{ mx: "auto" }} />
          </TableCell>
        )}
        <TableCell>
          <Skeleton width={250} />
        </TableCell>
      </TableRow>
    ));
  };

  return (
    <React.Fragment>
      <PageHeader
        helmetTitle="Payments 2025"
        title={
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 3,
            }}
          >
            Payments 2025
            <StatusChip
              tone="warn"
              label="For 2025 payments only"
              icon="warning"
            />
          </Box>
        }
        breadcrumbs={[{ label: "Payments" }, { label: "Payments 2025" }]}
        actions={
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontWeight: 500 }}
          >
            Tax year {TAX_YEAR}
          </Typography>
        }
      />

      <Callout tone="warn" title="2025 Tax Year Only" sx={{ mb: 4 }}>
        This page displays 1099 tax data from the CSIPayTax2025 database. Use
        this to provide 1099 forms to payees while production updates are
        pending.
      </Callout>

      <Card>
        <FilterBar>
          <SearchField
            value={searchTerm}
            onChange={setSearchTerm}
            label="Search 2025 payments"
            placeholder="Search by payee ID, name, address or amount"
          />
          {canDownload && (
            <>
              <Box sx={{ flexGrow: 1 }} />
              <LoadingButton
                variant="outlined"
                size="small"
                startIcon={<FileDownloadOutlined />}
                loadingPosition="start"
                onClick={fetchDataAndDownloadCSV}
                loading={loadingButton}
                sx={{ whiteSpace: "nowrap" }}
              >
                Download 2025 1099 summary (CSV)
              </LoadingButton>
            </>
          )}
        </FilterBar>
        {canDownload && (
          <>
            <Divider />
            <Box sx={{ px: 4, py: 3 }}>
              <DetailsExportCluster
                paymentCountFilter={paymentCountFilter}
                onPaymentCountFilterChange={handlePaymentCountFilterChange}
                clientCountFilter={clientCountFilter}
                onClientCountFilterChange={handleClientCountFilterChange}
                onDownload={fetchDataAndDownloadDetailCSV}
                loading={loadingButton}
                buttonLabel="Download 2025 payment details (CSV)"
              />
            </Box>
          </>
        )}
        <Divider />
        <TableContainer>
          <Table size="small" aria-label="2025 payments">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 56 }}>
                  <Box component="span" sx={visuallyHiddenSx}>
                    Details
                  </Box>
                </TableCell>
                <TableCell
                  sortDirection={orderBy === "payeeName" ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === "payeeName"}
                    direction={orderBy === "payeeName" ? order : "asc"}
                    onClick={() => handleRequestSort("payeeName")}
                  >
                    Payee
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy === "taxId" ? order : false}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <TableSortLabel
                      active={orderBy === "taxId"}
                      direction={orderBy === "taxId" ? order : "asc"}
                      onClick={() => handleRequestSort("taxId")}
                    >
                      Tax ID
                    </TableSortLabel>
                    {canReveal && (
                      <RevealAllButton
                        revealAll={reveal.revealAll}
                        onToggle={reveal.toggleAll}
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell
                  align="right"
                  sortDirection={orderBy === "amount" ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === "amount"}
                    direction={orderBy === "amount" ? order : "asc"}
                    onClick={() => handleRequestSort("amount")}
                  >
                    Amount
                  </TableSortLabel>
                </TableCell>
                {canDownload && <TableCell align="center">1099</TableCell>}
                <TableCell>Address</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading
                ? renderSkeletonRows()
                : data.map((row) => (
                    <ExpandableRow
                      key={row.id}
                      row={row}
                      canReveal={canReveal}
                      revealed={reveal.isRevealed(row.id)}
                      onToggleReveal={reveal.toggleRow}
                      canDownload={canDownload}
                      columnCount={columnCount}
                      isExpanded={expandedRows.has(row.id)}
                      isLoadingDetails={loadingDetails.has(row.id)}
                      detailsError={detailsError}
                      details={billDetails[row.id] || []}
                      onToggleExpand={handleToggleExpand}
                      onPdfError={notifyError}
                    />
                  ))}
              {!loading && loadError && (
                <TableRow>
                  <TableCell colSpan={columnCount} sx={{ py: 4 }}>
                    <Callout
                      tone="err"
                      role="alert"
                      title="Couldn't load 2025 payments"
                      action={
                        <Button color="inherit" size="small" onClick={retry}>
                          Retry
                        </Button>
                      }
                    >
                      Please retry, or contact support if this continues.
                    </Callout>
                  </TableCell>
                </TableRow>
              )}
              {!loading && !loadError && data.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={columnCount}
                    align="center"
                    sx={{ py: 8 }}
                  >
                    <Typography variant="body1" color="text.secondary">
                      No payments found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[25, 50, 100, 200]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>
    </React.Fragment>
  );
}

export default Taxes2025;
